import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '@store/helpers';
import { notificationInstance } from '@component/NotificationDropdown/helpers';
import {
  markSsoNotificationFetched,
  setUserNotification,
} from '@store/slices/notification/notification';
import { setIsNotificationRead } from '@store/slices/users/usersSlice';
import { logger } from '@utils/logger';
import { toastError } from '@utils/helpers';

export type GridPreferenceKey =
  | 'ASSIGNMENT_GRID'
  | 'CONTRACTOR_PORTAL_ASSIGNMENT_GRID';

export type GridPreferences = {
  columnOrder: string[];
  visibleColumns: string[];
  columnWidths: Record<string, number>;
};

const gridPreferencesUrl = (gridKey: GridPreferenceKey) =>
  `/v1/users/preferences/grid/${gridKey}`;

export const usersApi = createApi({
  reducerPath: 'Users',
  tagTypes: ['Users'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getUserNotification: builder.query({
      query: () => ({
        url: '/v1/users/notifications',
        params: { type: 'SSO_SETUP' },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const notification = notificationInstance(data);

          if (!notification) {
            dispatch(markSsoNotificationFetched());
            return;
          }
          dispatch(setIsNotificationRead(notification.isRead));
          dispatch(setUserNotification(notification));
          dispatch(markSsoNotificationFetched());
        } catch (err) {
          logger.log(err);
        }
      },
    }),
    readUserNotification: builder.mutation({
      query: ({ id }) => ({
        url: `/v1/users/notifications/${id}/read`,
        method: 'PUT',
      }),
    }),
    deleteUserNotification: builder.mutation({
      query: ({ id }) => ({
        url: `/v1/users/notifications/${id}`,
        method: 'DELETE',
      }),
    }),
    getGridPreferences: builder.query<
      GridPreferences | null,
      GridPreferenceKey
    >({
      query: gridPreferencesUrl,
      // an empty body or column order means the user never saved a layout
      transformResponse: (layout: GridPreferences | null) =>
        layout?.columnOrder?.length ? layout : null,
    }),
    saveGridPreferences: builder.mutation<
      GridPreferences,
      { gridKey: GridPreferenceKey; body: GridPreferences }
    >({
      query: ({ gridKey, body }) => ({
        url: gridPreferencesUrl(gridKey),
        method: 'PUT',
        body,
      }),
      // the cached layout is what the grid shows the next time it mounts
      async onQueryStarted({ gridKey, body }, { dispatch, queryFulfilled }) {
        dispatch(
          usersApi.util.upsertQueryData('getGridPreferences', gridKey, body)
        );
        try {
          await queryFulfilled;
        } catch (err) {
          toastError(err);
          // the save failed, so go back to what the server has
          dispatch(
            usersApi.endpoints.getGridPreferences.initiate(gridKey, {
              forceRefetch: true,
            })
          );
        }
      },
    }),
  }),
});

export const {
  useLazyGetUserNotificationQuery,
  useReadUserNotificationMutation,
  useDeleteUserNotificationMutation,
  useGetGridPreferencesQuery,
  useSaveGridPreferencesMutation,
} = usersApi;
